<script lang="ts">
	import { MediaQuery } from "svelte/reactivity";
	import copyIcon from "@core/assets/icons/copy.svg?raw";
	import facebookIcon from "@core/assets/icons/facebook.svg?raw";
	import linkedInIcon from "@core/assets/icons/linkedin.svg?raw";
	import shareIcon from "@core/assets/icons/share.svg?raw";
	import successCircleIcon from "@core/assets/icons/success-circle.svg?raw";
	import twitterIcon from "@core/assets/icons/twitter-x.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import Button from "./Button.svelte";
	import Icon from "./Icon.svelte";

	const isDesktop = new MediaQuery("(hover: hover)", false);
	let dropdownShown = $state(false);
	let copied = $state(false);

	$effect(() => {
		if (!copied) {
			return;
		}
		const timeout = setTimeout(() => {
			copied = false;
		}, 2000);
		return (): void => {
			clearTimeout(timeout);
		};
	});

	const iconMap = {
		facebook: facebookIcon,
		linkedin: linkedInIcon,
		twitter: twitterIcon,
	};

	async function share(platform: keyof typeof iconMap | "native"): Promise<void> {
		if (platform === "native") {
			await navigator.share({
				title: document.title,
				url: window.location.href,
			});
			return;
		}

		const url = encodeURIComponent(window.location.href);
		const title = encodeURIComponent(document.title);

		const shareLinks = {
			facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
			linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
			twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
		};

		window.open(shareLinks[platform], "_blank", "noopener,noreferrer");
	}

	async function shareButtonClick(): Promise<void> {
		if (isDesktop.current) {
			dropdownShown = !dropdownShown;
		} else {
			await share("native");
		}
	}

	$effect(() => {
		if (!isDesktop.current) {
			dropdownShown = false;
		}
	});
</script>

<div class="relative self-end">
	<Button
		variant={ButtonVariant.Primary}
		class="[@media(hover:hover)]:w-[16rem] justify-between"
		onclick={shareButtonClick}
	>
		Sdílet

		{#snippet icon()}
			<Icon icon={shareIcon} class="size-6" />
		{/snippet}
	</Button>
	{#if dropdownShown}
		<div
			class="w-full absolute text-2xs/[1.2] md:text-base/none uppercase text-white right-0 top-full mt-2 bg-cmcprimary rounded-lg shadow-lg overflow-hidden flex flex-col"
		>
			{#each Object.entries(iconMap) as [platform, iconSource] (platform)}
				<Button
					variant={ButtonVariant.Primary}
					class="min-w-full !rounded-none justify-between"
					onclick={(): void => void share(platform as keyof typeof iconMap)}
				>
					{platform}
					{#snippet icon()}
						<Icon icon={iconSource} class="size-6" />
					{/snippet}
				</Button>
			{/each}
			<Button
				variant={ButtonVariant.Primary}
				class={["min-w-full !rounded-none justify-between", copied && "!bg-cmcsuccess !text-cmctext"]}
				onclick={async (): Promise<void> => {
					if (!copied) {
						await navigator.clipboard.writeText(window.location.href);
						// eslint-disable-next-line require-atomic-updates
						copied = true;
					}
				}}
			>
				{copied ? "Zkopírováno!" : "Kopírovat odkaz"}
				{#snippet icon()}
					<Icon icon={copied ? successCircleIcon : copyIcon} class="size-6" />
				{/snippet}
			</Button>
		</div>
	{/if}
</div>
